import type { Breakpoint, BreakpointSettings } from '../../../components/player/player.interface';

import { useMemo } from 'react';

import { definedProps } from '../../../utils/defined-properties';

interface ElementsVisibilities {
  showLogo: boolean;
  showTitle: boolean;
  showTopbar: boolean;
  showPlayButton: boolean;
  showTimeline: boolean;
  showVolumeInput: boolean;
  showVolumeSlider: boolean;
  showVideoLength: boolean;
  showLoopButton: boolean;
  showSubtitles: boolean;
  showSettingsButton: boolean;
  showFullscreenButton: boolean;
  showPlaybackSpeed: boolean;
  showBigPlayButton: boolean;
  showStepButtons: boolean;
  showChaptersButton: boolean;
  showRelatedVideos: boolean;
  showTranscriptButton: boolean;
  showTranscriptDownload: boolean;
  verticalVolumeSlider: boolean;
  smallScreen: boolean;
}

const defaultBreakpoints: Breakpoint[] = [
  {
    maxWidth: 639,
    settings: {
      stepButtonsAreVisible: false,
      verticalVolumeSlider: true,
    },
  },
  {
    maxWidth: 360,
    settings: {
      hideLoopButton: true,
      hideSettingsMenu: true,
      hideChaptersButton: true,
      hideRelatedVideos: true,
    },
  },
];

// the minimum supported width for the player,
// below the player will use the settings for the smallest breakpoint (200-360px)
const MIN_SUPPORTED_WIDTH = 200;

/**
 * Returns the visibility of UI elements depending on the size of the player and initial configuration.
 * @param {number} [playerWidth] The current width of the player. @default [1280]
 * @param {BreakpointSettings} [settings] The initial configuration of the UI elements.
 * @param {Breakpoint[]} [customBreakpoints] The custom breakpoints to override the default configuration (or any default breakpoints implemented by the player).
 */
export function useElementsVisibilities(
  playerWidth = 1280,
  settings: BreakpointSettings = {},
  customBreakpoints: Breakpoint[] = []
): ElementsVisibilities {
  // Apply default breakpoints
  const mergedBreakpoints = useMemo(() => [...defaultBreakpoints, ...customBreakpoints], [customBreakpoints]);

  // Apply breakpoint settings
  const currentSettings = useMemo(() => {
    return mergedBreakpoints.reduce(
      (acc, breakpoint) => {
        const withinMin =
          !breakpoint.minWidth || playerWidth >= breakpoint.minWidth || breakpoint.minWidth <= MIN_SUPPORTED_WIDTH;
        const withinMax = !breakpoint.maxWidth || playerWidth <= breakpoint.maxWidth;

        return withinMin && withinMax ? { ...acc, ...definedProps(breakpoint.settings) } : acc;
      },
      { ...settings }
    );
  }, [playerWidth, settings, mergedBreakpoints]);

  const smallScreen = playerWidth < 480;

  // Map current settings to elements visibilities
  const visibilities = useMemo(() => {
    return {
      showLogo: !currentSettings.hideLogo,
      showTitle: !currentSettings.hideTitle,
      showTopbar: !currentSettings.hideTopbar,
      showPlayButton: !currentSettings.hidePlayButton,
      showTimeline: !currentSettings.hideTimeline,
      showVolumeInput: !currentSettings.hideVolumeInput,
      showVolumeSlider: !currentSettings.hideVolumeInput && !currentSettings.hideVolumeSlider,
      showVideoLength: !currentSettings.hideTimer,
      showLoopButton: !currentSettings.hideLoopButton,
      showSubtitles: !currentSettings.hideLanguageMenu,
      showSettingsButton: !currentSettings.hideSettingsMenu && !currentSettings.hideMenu,
      showFullscreenButton: !currentSettings.hideFullscreenButton && !currentSettings.hideMenu,
      showPlaybackSpeed: !currentSettings.hidePlaybackSpeed,
      showBigPlayButton: !currentSettings.hideBigPlayButton,
      showChaptersButton: !currentSettings.hideChaptersButton,
      showStepButtons: !!currentSettings.stepButtonsAreVisible,
      showTranscriptButton: !currentSettings.hideTranscriptButton,
      showTranscriptDownload: !currentSettings.hideTranscriptDownload,
      verticalVolumeSlider:
        !!currentSettings.verticalVolumeSlider &&
        !currentSettings.hideVolumeInput &&
        !currentSettings.hideVolumeSlider &&
        playerWidth > 0,
      showRelatedVideos: !currentSettings.hideRelatedVideos,
      smallScreen,
    } as ElementsVisibilities;
  }, [
    currentSettings.hideLogo,
    currentSettings.hideTitle,
    currentSettings.hideTopbar,
    currentSettings.hidePlayButton,
    currentSettings.hideTimeline,
    currentSettings.hideVolumeInput,
    currentSettings.hideVolumeSlider,
    currentSettings.hideTimer,
    currentSettings.hideLoopButton,
    currentSettings.hideLanguageMenu,
    currentSettings.hideSettingsMenu,
    currentSettings.hideMenu,
    currentSettings.hideFullscreenButton,
    currentSettings.hidePlaybackSpeed,
    currentSettings.hideBigPlayButton,
    currentSettings.hideChaptersButton,
    currentSettings.stepButtonsAreVisible,
    currentSettings.hideTranscriptButton,
    currentSettings.hideTranscriptDownload,
    currentSettings.verticalVolumeSlider,
    currentSettings.hideRelatedVideos,
    playerWidth,
    smallScreen,
  ]);

  return visibilities;
}
